import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Circle, FileText, Edit, Upload, Shield } from 'lucide-react';
import { PageWrapper } from '../components/PageWrapper';

export const Help = () => {
  const navigate = useNavigate();

  const sections = [
    {
      icon: Check,
      title: 'Goals & Easy Goals',
      content: `On the Daily Log screen, your goals appear as simple checkboxes.

Each goal shows its text — for example:
  [] Go for a 20 minute walk
  [] Sit in the garden

Tick the checkbox when you complete a goal. If you have an easy version, it appears as a separate checkbox underneath.

You can tick one, both, or neither. If neither is ticked, the goal is recorded as not done for that day. Even ticking the easy goal is a win — it means you showed up.`
    },
    {
      icon: Edit,
      title: 'Editing Past Entries',
      content: `You can edit any past entry at any time:

1. Go to the History tab
2. Tap on any past entry
3. Make your changes
4. Tap Save

Your changes will be saved immediately. There's no penalty or judgment for editing — this app is about supporting your rebuild, not tracking perfection.`
    },
    {
      icon: FileText,
      title: 'Reflections & Custom Questions',
      content: `RE-BUILD-ME comes with 5 default reflection questions. You can:

• Edit any question to make it your own
• Add up to 5 more custom questions (10 total)
• Hide questions you don't use
• Reorder them however you like
• Reset to the original defaults at any time

Your answers are stored safely regardless of question changes — editing a question text won't affect your past entries.`
    },
    {
      icon: Upload,
      title: 'Exporting Your Data',
      content: `You can export your data in multiple formats:

• CSV: A spreadsheet format for Excel or Google Sheets.

• PDF: A printable document with all your reflections and goal progress.

• JSON Backup: A complete backup of all your data and settings. Use this to keep a backup, restore on a new device, or transfer between devices.

To restore, go to Export → Restore from Backup and select your JSON file.`
    },
    {
      icon: Shield,
      title: 'Data Privacy',
      content: `Your privacy is our priority:

• All data is stored locally on your device only
• No accounts, emails, or sign-ups required
• No advertising, analytics, or tracking
• No data is ever sent to any server

Important: If you delete the app, all data is lost. Use the backup feature in Export to keep a copy regularly.`
    },
  ];

  return (
    <PageWrapper className="bg-app">
      <div className="max-w-md mx-auto px-6 py-6">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 rounded-full hover:bg-wisteria-50 transition-colors"
            data-testid="back-btn"
            aria-label="Go back"
          >
            <ArrowLeft size={24} strokeWidth={1.5} className="text-secondary" />
          </button>
          <h1 className="font-heading text-2xl font-light text-primary" data-testid="help-title">
            Help & Instructions
          </h1>
        </div>

        <div className="space-y-4">
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary"
              data-testid={`help-section-${index}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-wisteria-100 flex items-center justify-center">
                  <section.icon size={20} style={{ color: 'var(--theme-accent, #6B5B95)' }} />
                </div>
                <h2 className="font-body font-medium text-primary">{section.title}</h2>
              </div>
              <p className="font-body text-secondary text-sm leading-relaxed whitespace-pre-line">
                {section.content}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="mt-8 text-center"
        >
          <p className="font-body text-sm text-secondary/60">
            RE-BUILD-ME — Your private daily reset
          </p>
        </motion.div>
      </div>
    </PageWrapper>
  );
};

export default Help;
